import * as React from 'react';
import { EStarSize, EStarTheme, getPrettyFloat, Star } from '@cian/frontend-reviews-components';
import { UIText1, UIText2, ArticleHeading0, UIHeading4 } from '@cian/ui-kit';
import * as DOMPurify from 'isomorphic-dompurify';

import * as styles from './styles.css';
import { trackTransferToDeveloperCard } from '../../containers/Builder/tracking';
import { IBuilderReviewStats } from '../../types/newbuilding/builder';
import { HoveredLink } from '../HoveredLink';
import { Layout } from '../Layout';

export interface IBuilderProps {
  logoUrl: string;
  name: string;
  yearOfFoundation?: number | null;
  done: React.ReactNode;
  inProgress: React.ReactNode;
  description: string | null;
  url?: string;
  onBuilderLinkClick(): void;
  reviewStats?: IBuilderReviewStats;
  housesBuiltInTime?: string;
}

export function Builder({
  logoUrl,
  name,
  yearOfFoundation,
  done,
  inProgress,
  description,
  url,
  onBuilderLinkClick,
  housesBuiltInTime,
  reviewStats,
}: IBuilderProps) {
  const rate = React.useMemo(() => {
    return reviewStats?.totalRate ? getPrettyFloat(reviewStats?.totalRate) : '';
  }, [reviewStats]);

  return (
    <Layout>
      <div className={styles['nav_container']} id="developer">
        <div className={styles['container']}>
          <div className={styles['builder']}>
            {logoUrl && (
              <a
                className={styles['logo-link']}
                href={url}
                target="_blank"
                rel="noreferrer"
                onClick={trackTransferToDeveloperCard}
              >
                <div
                  className={styles['logo']}
                  data-testid="BuilderLogo"
                  style={{ backgroundImage: `url(${logoUrl})` }}
                />
              </a>
            )}
            <div className={styles['title']}>
              <UIText1 color="gray60_100" data-testid="BuilderTitle">
                Застройщик
              </UIText1>
              <a
                className={styles['name']}
                href={url}
                target="_blank"
                rel="noreferrer"
                onClick={trackTransferToDeveloperCard}
              >
                <ArticleHeading0 as="h2" data-testid="BuilderName">
                  {name}
                </ArticleHeading0>
              </a>
              {reviewStats && (
                <div className={styles['review']} data-testid="BuilderReviewStats">
                  <span className={styles['review-rate']}>
                    <UIHeading4 color="current_color">{rate}</UIHeading4>
                  </span>
                  <Star theme={EStarTheme.Fillable} size={EStarSize.M} value={reviewStats.totalRate}></Star>
                  <span className={styles['review-text']}>
                    <UIText1 color="current_color">{reviewStats.reviewCountText}</UIText1>
                  </span>
                </div>
              )}
            </div>
          </div>
          <div>
            <div className={styles['statistic']}>
              {yearOfFoundation && (
                <div className={styles['statistic-item']} data-testid="BuilderStatsYearOfFoundation">
                  <ArticleHeading0>{yearOfFoundation}</ArticleHeading0>
                  <div className={styles['statistic-item-text']}>
                    <UIText2 fontWeight="bold">
                      год
                      <br />
                      основания
                    </UIText2>
                  </div>
                </div>
              )}
              <div className={styles['statistic-item']} data-testid="BuilderStatsDone">
                {done}
              </div>
              <div className={styles['statistic-item']} data-testid="BuilderStatsInProgress">
                {inProgress}
              </div>
              {housesBuiltInTime && (
                <div className={styles['statistic-item']} data-testid="BuilderStatsInTime">
                  <ArticleHeading0>{housesBuiltInTime}</ArticleHeading0>
                  <div className={styles['statistic-item-text']}>
                    <UIText2 fontWeight="bold">
                      домов сдано
                      <br />
                      вовремя
                    </UIText2>
                  </div>
                </div>
              )}
            </div>
            {description && (
              <div className={styles['description']}>
                <UIText1 data-testid="BuilderDescriptionText">
                  <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
                  />
                </UIText1>
              </div>
            )}
            {url && (
              <HoveredLink url={url} childNode={UIText2} onClick={onBuilderLinkClick}>
                Узнать больше о застройщике
              </HoveredLink>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
